import { ArrayField, BooleanField, NumberField, SingleFieldList, TextField } from 'react-admin';

import { GetInstrumentOneResponse } from '@Common/types/bo/instruments';

import { ShowFields, ShowSection } from '@sh4res/bo-common/components';
import {
  fundinfoPricingFrequencyChoices,
  managementTypeChoices,
  mutualFundAssetClassChoices,
} from '@sh4res/bo-common/components/Instruments/choices';
import { CountriesStatusList } from '@sh4res/bo-common/components/Instruments/countries_status/CountriesStatusList';
import { InstrumentRiskField } from '@sh4res/bo-common/components/Instruments/risk/InstrumentRiskField';
import {
  CurrencyField,
  IdField,
  LinkField,
  SelectChipField,
  UTCDateField,
} from '@sh4res/bo-common/fields';

export const MutualFundSection = ({ isExternalBo }: { isExternalBo: boolean }) => (
  <>
    {!isExternalBo && (
      <ShowSection label="resources.bo_common.instruments.sections.statuses">
        <ShowFields<GetInstrumentOneResponse & { country_settings: string }>
          fields={{
            is_price_fetched: (
              <BooleanField
                source=""
                data-testid="BoExternalMutualFundSection_IsPriceFetchedField"
              />
            ),
            is_enabled: (
              <BooleanField source="" data-testid="BoExternalMutualFundSection_IsEnabledField" />
            ),
            country_settings: <CountriesStatusList mode="list" />,
          }}
        />
      </ShowSection>
    )}
    <ShowSection label="resources.bo_common.instruments.sections.documents">
      <ShowFields<GetInstrumentOneResponse>
        fields={{
          prospectus_url: (
            <LinkField
              source="prospectus_url"
              data-testid="BoExternalMutualFundSection_ProspectusUrlLinkField"
            />
          ),
          kid_urls: (
            <ArrayField data-testid="BoExternalMutualFundSection_KidUrlsArrayField">
              <SingleFieldList linkType={false}>
                <LinkField
                  source="language"
                  hrefSource="url"
                  data-testid="BoExternalMutualFundSection_KidUrlsLinkField"
                />
              </SingleFieldList>
            </ArrayField>
          ),
        }}
      />
    </ShowSection>
    <ShowSection label="resources.bo_common.instruments.sections.other">
      <ShowFields<GetInstrumentOneResponse>
        fields={{
          risk: <InstrumentRiskField data-testid="BoExternalMutualFundSection_RiskField" />,
          mutual_fund_asset_class: (
            <SelectChipField
              choices={mutualFundAssetClassChoices}
              data-testid="BoExternalMutualFundSection_MutualFundAssetClassField"
            />
          ),
          support_code: <TextField data-testid="BoExternalMutualFundSection_SupportCodeField" />,
          management_company: (
            <TextField data-testid="BoExternalMutualFundSection_ManagementCompanyField" />
          ),
          management_fee_pct: (
            <TextField data-testid="BoExternalMutualFundSection_ManagementFeePctField" />
          ),
          management_type: (
            <SelectChipField
              choices={managementTypeChoices}
              data-testid="BoExternalMutualFundSection_ManagementTypeField"
            />
          ),
          last_nav_date: (
            <UTCDateField data-testid="BoExternalMutualFundSection_LastNavDateField" />
          ),
          last_nav: (
            <CurrencyField currency="EUR" testId="BoExternalMutualFundSection_LastNavField" />
          ),
          qty_decimal_places: (
            <NumberField data-testid="BoExternalMutualFundSection_QtyDecimalPlacesField" />
          ),
          pricing_frequency: (
            <SelectChipField
              choices={fundinfoPricingFrequencyChoices}
              data-testid="BoExternalMutualFundSection_PricingFrequencyField"
            />
          ),
          redemption_period_start_at: (
            <UTCDateField data-testid="BoExternalMutualFundSection_RedemptionPeriodStartAtField" />
          ),
          redemption_period_end_at: (
            <UTCDateField data-testid="BoExternalMutualFundSection_RedemptionPeriodEndAtField" />
          ),
          maturity_target_instrument_id: (
            <IdField
              reference="instruments"
              data-testid="BoExternalMutualFundSection_MaturityTargetInstrumentIdField"
            />
          ),
          is_triggered_arbitrage_allowed: (
            <BooleanField data-testid="BoExternalMutualFundSection_IsTriggeredArbitrageAllowedField" />
          ),
          is_single_underlying_asset: (
            <BooleanField data-testid="BoExternalMutualFundSection_IsSingleUnderlyingAssetField" />
          ),
          is_withdrawal_trigger_threshold_enabled: (
            <BooleanField data-testid="BoExternalMutualFundSection_IsWithdrawalTriggerThresholdEnabledField" />
          ),
          is_floor_nav_enabled: (
            <BooleanField data-testid="BoExternalMutualFundSection_IsFloorNavEnabledField" />
          ),
          is_floor_price_enabled: (
            <BooleanField data-testid="BoExternalMutualFundSection_IsFloorPriceEnabledField" />
          ),
        }}
      />
    </ShowSection>
    <ShowSection label="resources.bo_common.instruments.sections.cut_offs">
      <ShowFields<GetInstrumentOneResponse>
        subtitles={{
          nav_cut_off_buy: 'resources.bo_common.instruments.tooltips.nav_cut_off',
          nav_cut_off_sell: 'resources.bo_common.instruments.tooltips.nav_cut_off',
          admin_cut_off_buy: 'resources.bo_common.instruments.tooltips.admin_cut_off',
          admin_cut_off_sell: 'resources.bo_common.instruments.tooltips.admin_cut_off',
          announcement_delay_buy: 'resources.bo_common.instruments.tooltips.announcement_delay',
          announcement_delay_sell: 'resources.bo_common.instruments.tooltips.announcement_delay',
        }}
        fields={{
          nav_cut_off_buy: (
            <TextField data-testid="BoExternalMutualFundSection_NavCutOffBuyField" />
          ),
          nav_cut_off_sell: (
            <TextField data-testid="BoExternalMutualFundSection_NavCutOffSellField" />
          ),
          admin_cut_off_buy: (
            <TextField data-testid="BoExternalMutualFundSection_AdminCutOffBuyField" />
          ),
          admin_cut_off_sell: (
            <TextField data-testid="BoExternalMutualFundSection_AdminCutOffSellField" />
          ),
          ...(!isExternalBo && {
            announcement_delay_buy: (
              <NumberField data-testid="BoExternalMutualFundSection_AnnouncementDelayBuyField" />
            ),
            announcement_delay_sell: (
              <NumberField data-testid="BoExternalMutualFundSection_AnnouncementDelaySellField" />
            ),
          }),
        }}
      />
    </ShowSection>
  </>
);
